/* LUMBV product page scripts.
   Judge.me review stars are handled by Liquid + CSS so the widget keeps its real data and async app rendering is not mutated here. */

(() => {
  const BUTTON_SELECTOR = '[data-lumbv-recommendations-prev], [data-lumbv-recommendations-next]';
  const root = window.Shopify?.routes?.root || '/';

  async function parseCartResponse(response) {
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
    return data;
  }

  function formatMoney(cents, currency, locale) {
    return new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(cents / 100);
  }

  function updateBundle(section) {
    const selects = [...section.querySelectorAll('[data-bundle-variant]')];
    const quantity = Math.max(1, Number(section.querySelector('[data-bundle-quantity]')?.value || 1));
    const discountReady = section.dataset.discountReady === 'true';
    const discountPercent = Number(section.dataset.discountPercent || 0);
    const factor = discountReady ? (100 - discountPercent) / 100 : 1;
    const currency = section.dataset.currency;
    const locale = section.dataset.locale;
    let originalTotal = 0;

    selects.forEach((select) => {
      const price = Number(select.selectedOptions[0]?.dataset.price || 0);
      originalTotal += price;
      const card = select.closest('.lumbv-product-bundle__card');
      const cardPriceNode = card?.querySelector('[data-card-price]');
      if (cardPriceNode) cardPriceNode.textContent = formatMoney(price, currency, locale);
    });

    const originalNode = section.querySelector('[data-bundle-original-total]');
    const discountedNode = section.querySelector('[data-bundle-discounted-total]');
    if (originalNode) originalNode.textContent = formatMoney(originalTotal * quantity, currency, locale);
    if (discountedNode) discountedNode.textContent = formatMoney(Math.round(originalTotal * factor) * quantity, currency, locale);
  }

  async function addBundleToCart(bundleButton) {
    const section = bundleButton.closest('[data-lumbv-bundle]');
    const selects = [...section.querySelectorAll('[data-bundle-variant]')];
    const quantity = Math.max(1, Number(section.querySelector('[data-bundle-quantity]')?.value || 1));
    const status = section.querySelector('[data-bundle-status]');
    const ids = selects.map((select) => Number(select.value));

    if (ids.length !== 2 || ids[0] === ids[1]) {
      if (status) status.textContent = section.dataset.differentProductsLabel;
      return;
    }

    bundleButton.disabled = true;
    bundleButton.setAttribute('aria-busy', 'true');
    bundleButton.textContent = bundleButton.dataset.loadingLabel;
    if (status) status.textContent = '';

    try {
      await parseCartResponse(
        await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: ids.map((id) => ({
              id,
              quantity,
              properties: { _lumbv_bundle: section.id },
            })),
          }),
        }),
      );
      window.location.assign(`${root}cart`);
    } catch (error) {
      if (status) status.textContent = error.message;
      bundleButton.disabled = false;
      bundleButton.removeAttribute('aria-busy');
      bundleButton.textContent = bundleButton.dataset.idleLabel;
    }
  }

  function changeBundleQuantity(quantityButton) {
    const section = quantityButton.closest('[data-lumbv-bundle]');
    const input = section.querySelector('[data-bundle-quantity]');
    const change = quantityButton.matches('[data-bundle-quantity-plus]') ? 1 : -1;
    input.value = Math.min(Number(input.max), Math.max(Number(input.min), Number(input.value || 1) + change));
    updateBundle(section);
  }

  function getSlider(button) {
    return button.closest('slideshow-component')?.querySelector('.complementary-slider');
  }

  function getMaxScrollLeft(slider) {
    return Math.max(0, slider.scrollWidth - slider.clientWidth);
  }

  function getScrollStep(slider) {
    return Math.max(180, Math.round(slider.clientWidth * 0.82));
  }

  function updateButtons(slider) {
    const component = slider.closest('slideshow-component');
    const prev = component?.querySelector('[data-lumbv-recommendations-prev]');
    const next = component?.querySelector('[data-lumbv-recommendations-next]');

    if (!prev || !next) return;

    const maxScrollLeft = getMaxScrollLeft(slider);
    const canScroll = maxScrollLeft > 2;

    prev.disabled = !canScroll || slider.scrollLeft <= 2;
    next.disabled = !canScroll || slider.scrollLeft >= maxScrollLeft - 2;
  }

  function scrollRecommendation(button) {
    const slider = getSlider(button);
    if (!slider) return;

    const direction = button.hasAttribute('data-lumbv-recommendations-prev') ? -1 : 1;
    const maxScrollLeft = getMaxScrollLeft(slider);
    const targetLeft = Math.max(0, Math.min(maxScrollLeft, slider.scrollLeft + getScrollStep(slider) * direction));

    slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
    window.setTimeout(() => updateButtons(slider), 320);
  }

  function setupSlider(slider) {
    if (!slider || slider.dataset.lumbvRecommendationsReady === 'true') return;

    slider.dataset.lumbvRecommendationsReady = 'true';
    slider.addEventListener('scroll', () => window.requestAnimationFrame(() => updateButtons(slider)), { passive: true });
    window.addEventListener('resize', () => updateButtons(slider), { passive: true });
    window.requestAnimationFrame(() => updateButtons(slider));
    window.setTimeout(() => updateButtons(slider), 450);
    window.setTimeout(() => updateButtons(slider), 1200);
  }

  function setupAllSliders(root = document) {
    root.querySelectorAll('product-recommendations .complementary-slider').forEach(setupSlider);
  }
  function getStandaloneRecommendationsSection(button) {
    return button.closest('.lumbv-recommendations');
  }

  function getStandaloneTrack(button) {
    return getStandaloneRecommendationsSection(button)?.querySelector('[data-recommendations-track]');
  }

  function ensureStandaloneControls(section) {
    const track = section?.querySelector('[data-recommendations-track]');
    if (!section || !track) return;

    let header = section.querySelector('.lumbv-recommendations__header');
    if (!header) {
      header = document.createElement('div');
      header.className = 'lumbv-recommendations__header';
      const title = document.createElement('h2');
      title.textContent = 'Recommended products';
      header.appendChild(title);
      const productRecommendations = section.querySelector('product-recommendations');
      productRecommendations?.insertBefore(header, productRecommendations.firstElementChild);
    }

    if (header.querySelector('.lumbv-recommendations__controls')) return;

    const controls = document.createElement('div');
    controls.className = 'lumbv-recommendations__controls';

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.setAttribute('data-recommendations-direction', 'previous');
    previous.setAttribute('aria-label', 'Previous products');
    previous.textContent = '��';

    const next = document.createElement('button');
    next.type = 'button';
    next.setAttribute('data-recommendations-direction', 'next');
    next.setAttribute('aria-label', 'Next products');
    next.textContent = '��';

    controls.append(previous, next);
    header.appendChild(controls);
  }
  function updateStandaloneButtons(section) {
    const track = section?.querySelector('[data-recommendations-track]');
    const prev = section?.querySelector('[data-recommendations-direction="previous"]');
    const next = section?.querySelector('[data-recommendations-direction="next"]');
    if (!track || !prev || !next) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const canScroll = maxScrollLeft > 2;
    prev.disabled = !canScroll || track.scrollLeft <= 2;
    next.disabled = !canScroll || track.scrollLeft >= maxScrollLeft - 2;
  }

  function scrollStandaloneRecommendations(button) {
    const track = getStandaloneTrack(button);
    if (!track) return;

    const direction = button.dataset.recommendationsDirection === 'previous' ? -1 : 1;
    const step = Math.max(180, Math.round(track.clientWidth * 0.82));
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const targetLeft = Math.max(0, Math.min(maxScrollLeft, track.scrollLeft + step * direction));
    track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    const section = button.closest('.lumbv-recommendations');
    window.setTimeout(() => updateStandaloneButtons(section), 120);
    window.setTimeout(() => updateStandaloneButtons(section), 360);
    window.setTimeout(() => updateStandaloneButtons(section), 760);
  }

  function setupStandaloneRecommendations(section) {
    ensureStandaloneControls(section);
    const track = section?.querySelector('[data-recommendations-track]');
    if (!track || track.dataset.lumbvStandaloneRecommendationsReady === 'true') return;

    track.dataset.lumbvStandaloneRecommendationsReady = 'true';
    track.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => updateStandaloneButtons(section));
      window.setTimeout(() => updateStandaloneButtons(section), 180);
    }, { passive: true });
    window.addEventListener('resize', () => updateStandaloneButtons(section), { passive: true });
    window.requestAnimationFrame(() => updateStandaloneButtons(section));
    window.setTimeout(() => updateStandaloneButtons(section), 450);
    window.setTimeout(() => updateStandaloneButtons(section), 1200);
  }

  function setupAllStandaloneRecommendations(root = document) {
    root.querySelectorAll('.lumbv-recommendations').forEach(setupStandaloneRecommendations);
  }
  async function loadStandaloneRecommendations(container) {
    if (!container || container.dataset.lumbvRecommendationsLoaded === 'true' || container.dataset.lumbvRecommendationsLoading === 'true') return;
    if (container.querySelector('[data-recommendations-track]')) {
      container.dataset.lumbvRecommendationsLoaded = 'true';
      setupStandaloneRecommendations(container.closest('.lumbv-recommendations'));
      return;
    }

    const sectionId = container.dataset.sectionId;
    const productId = container.dataset.productId;
    const baseUrl = container.dataset.url;
    if (!sectionId || !productId || !baseUrl) return;

    container.dataset.lumbvRecommendationsLoading = 'true';

    try {
      const separator = baseUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${baseUrl}${separator}section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}`);
      if (!response.ok) return;

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const replacement = html.querySelector('product-recommendations') || html.querySelector('.lumbv-recommendations product-recommendations');
      if (!replacement || !replacement.innerHTML.trim()) return;

      container.innerHTML = replacement.innerHTML;
      container.dataset.lumbvRecommendationsLoaded = 'true';
      setupStandaloneRecommendations(container.closest('.lumbv-recommendations'));
    } finally {
      container.dataset.lumbvRecommendationsLoading = 'false';
    }
  }

  function loadAllStandaloneRecommendations(root = document) {
    root.querySelectorAll('.lumbv-recommendations product-recommendations').forEach(loadStandaloneRecommendations);
  }

  document.addEventListener('change', (event) => {
    const bundle = event.target.closest('[data-lumbv-bundle]');
    if (bundle && (event.target.matches('[data-bundle-variant]') || event.target.matches('[data-bundle-quantity]'))) {
      updateBundle(bundle);
    }
  });

  document.addEventListener('click', (event) => {
    const bundleButton = event.target.closest('[data-bundle-submit]');
    if (bundleButton) {
      event.preventDefault();
      event.stopPropagation();
      addBundleToCart(bundleButton);
      return;
    }

    const quantityButton = event.target.closest('[data-bundle-quantity-minus], [data-bundle-quantity-plus]');
    if (quantityButton) {
      event.preventDefault();
      event.stopPropagation();
      changeBundleQuantity(quantityButton);
      return;
    }

    const standaloneButton = event.target.closest('[data-recommendations-direction]');
    if (standaloneButton) {
      event.preventDefault();
      event.stopPropagation();
      scrollStandaloneRecommendations(standaloneButton);
      return;
    }

    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    scrollRecommendation(button);
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    setupAllSliders(event.target);
    loadAllStandaloneRecommendations(event.target);
    setupAllStandaloneRecommendations(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupAllSliders();
      loadAllStandaloneRecommendations();
      setupAllStandaloneRecommendations();
    }, { once: true });
  } else {
    setupAllSliders();
    loadAllStandaloneRecommendations();
    setupAllStandaloneRecommendations();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          setupAllSliders(node);
          loadAllStandaloneRecommendations(node);
          setupAllStandaloneRecommendations(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();